import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    where,
    orderBy,
    limit,
    updateDoc,
    Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';

export interface PublicUserProfile {
    uid: string;
    name: string;
    email: string;
    nickname?: string;
    profilePictureUrl?: string;
    bio?: string;
    createdAt: Date;
    postsCount?: number;
    followersCount?: number;
    followingCount?: number;
}

/**
 * Busca perfil público de um usuário
 */
export const getUserProfile = async (userId: string): Promise<PublicUserProfile | null> => {
    try {
        const userDoc = await getDoc(doc(db, 'users', userId));

        if (!userDoc.exists()) {
            return null;
        }

        const data = userDoc.data();

        // Buscar contagens
        const postsQuery = query(collection(db, 'posts'), where('userId', '==', userId));
        const postsSnapshot = await getDocs(postsQuery);

        return {
            uid: userDoc.id,
            name: data.name || '',
            email: data.email || '',
            nickname: data.nickname || '',
            profilePictureUrl: data.profilePictureUrl,
            bio: data.bio || '',
            createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
            postsCount: postsSnapshot.size,
            followersCount: (data.followers || []).length,
            followingCount: (data.following || []).length,
        };
    } catch (error) {
        console.error('Erro ao buscar perfil:', error);
        return null;
    }
};

/**
 * Busca usuários por nome ou email
 */
export const searchUsers = async (searchQuery: string, limitCount: number = 20): Promise<PublicUserProfile[]> => {
    try {
        if (!searchQuery.trim()) {
            return [];
        }

        const usersRef = collection(db, 'users');

        // Buscar por nome (case insensitive seria ideal mas Firestore não suporta nativamente)
        const q = query(
            usersRef,
            where('name', '>=', searchQuery),
            where('name', '<=', searchQuery + '\uf8ff'),
            limit(limitCount)
        );

        const snapshot = await getDocs(q);

        const users: PublicUserProfile[] = [];
        for (const userDoc of snapshot.docs) {
            const data = userDoc.data();

            // Buscar contagem de posts
            const postsQuery = query(collection(db, 'posts'), where('userId', '==', userDoc.id));
            const postsSnapshot = await getDocs(postsQuery);

            users.push({
                uid: userDoc.id,
                name: data.name || '',
                email: data.email || '',
                profilePictureUrl: data.profilePictureUrl,
                bio: data.bio || '',
                createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
                postsCount: postsSnapshot.size,
                followersCount: (data.followers || []).length,
                followingCount: (data.following || []).length,
            });
        }

        return users;
    } catch (error) {
        console.error('Erro ao buscar usuários:', error);
        return [];
    }
};

/**
 * Busca usuários sugeridos (que você ainda não segue)
 */
export const getSuggestedUsers = async (
    currentUserId: string,
    limitCount: number = 10
): Promise<PublicUserProfile[]> => {
    try {
        // Buscar quem o usuário já segue
        const currentUserDoc = await getDoc(doc(db, 'users', currentUserId));
        const following = currentUserDoc.exists() ? (currentUserDoc.data().following || []) : [];

        // Buscar todos os usuários (limitado)
        const usersRef = collection(db, 'users');
        const q = query(usersRef, orderBy('createdAt', 'desc'), limit(limitCount * 2));
        const snapshot = await getDocs(q);

        const suggestions: PublicUserProfile[] = [];

        for (const userDoc of snapshot.docs) {
            // Pular o próprio usuário e quem já segue
            if (userDoc.id === currentUserId || following.includes(userDoc.id)) {
                continue;
            }

            if (suggestions.length >= limitCount) {
                break;
            }

            const data = userDoc.data();

            // Buscar contagem de posts
            const postsQuery = query(collection(db, 'posts'), where('userId', '==', userDoc.id));
            const postsSnapshot = await getDocs(postsQuery);

            suggestions.push({
                uid: userDoc.id,
                name: data.name || '',
                email: data.email || '',
                profilePictureUrl: data.profilePictureUrl,
                bio: data.bio || '',
                createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
                postsCount: postsSnapshot.size,
                followersCount: (data.followers || []).length,
                followingCount: (data.following || []).length,
            });
        }

        return suggestions;
    } catch (error) {
        console.error('Erro ao buscar sugestões:', error);
        return [];
    }
};

/**
 * Atualiza a bio do usuário
 */
export const updateUserBio = async (userId: string, bio: string): Promise<void> => {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            bio: bio.trim(),
        });
    } catch (error) {
        console.error('Erro ao atualizar bio:', error);
        throw error;
    }
};

/**
 * Busca todos os usuários (para admin/debug)
 */
export const getAllUsers = async (limitCount: number = 50): Promise<PublicUserProfile[]> => {
    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, orderBy('createdAt', 'desc'), limit(limitCount));
        const snapshot = await getDocs(q);

        const users: PublicUserProfile[] = [];

        for (const userDoc of snapshot.docs) {
            const data = userDoc.data();

            // Buscar contagem de posts
            const postsQuery = query(collection(db, 'posts'), where('userId', '==', userDoc.id));
            const postsSnapshot = await getDocs(postsQuery);

            users.push({
                uid: userDoc.id,
                name: data.name || '',
                email: data.email || '',
                profilePictureUrl: data.profilePictureUrl,
                bio: data.bio || '',
                createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
                postsCount: postsSnapshot.size,
                followersCount: (data.followers || []).length,
                followingCount: (data.following || []).length,
            });
        }

        return users;
    } catch (error) {
        console.error('Erro ao buscar todos os usuários:', error);
        return [];
    }
};
